

import React from 'react';
import { ProjectWithZones, ProjectStatus } from '@/types';
import { Button } from '@/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shared/ui/select';
import { Edit, CheckCircle2, Clock, Archive } from 'lucide-react';

interface ProjectSectionHeaderProps {
  project: ProjectWithZones;
  isReadOnly: boolean;
  editMode: boolean;
  onEditToggle: () => void;
  onStatusChange: (status: ProjectStatus) => void;
  currentStatus: ProjectStatus;
}

export const ProjectSectionHeader: React.FC<ProjectSectionHeaderProps> = ({
  project,
  isReadOnly,
  editMode,
  onEditToggle,
  onStatusChange,
  currentStatus,
}) => {
  const handleStatusChange = (value: string) => {
    onStatusChange(value as ProjectStatus);
  };

  const getStatusIcon = (status: ProjectStatus) => {
    switch (status) {
      case '完了':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case '進行中':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case '中止':
        return <Archive className="h-4 w-4 text-gray-500" />;
      case '保留':
        return <Archive className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: ProjectStatus) => {
    return status;
  };

  return (
    <div className="flex flex-row justify-between items-center">
      <div className="flex-1 min-w-0">
        <h2 className="fluid-base md:text-lg font-semibold truncate">{project.name}</h2>
      </div>

      <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
        {!isReadOnly && (
          <div className="min-w-[80px] sm:min-w-[100px]">
            {editMode ? (
              <Select
                value={currentStatus}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="w-full text-xs sm:text-sm h-8 sm:h-9">
                  <SelectValue placeholder="ステータス" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="進行中">
                    <div className="flex items-center">
                      <Clock className="mr-2 h-4 w-4 text-blue-500" />
                      進行中
                    </div>
                  </SelectItem>
                  <SelectItem value="完了">
                    <div className="flex items-center">
                      <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                      完了
                    </div>
                  </SelectItem>
                  <SelectItem value="保留">
                    <div className="flex items-center">
                      <Archive className="mr-2 h-4 w-4 text-yellow-500" />
                      保留
                    </div>
                  </SelectItem>
                  <SelectItem value="中止">
                    <div className="flex items-center">
                      <Archive className="mr-2 h-4 w-4 text-gray-500" />
                      中止
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center text-xs sm:text-sm px-2 sm:px-3 py-2 border rounded-md bg-background h-8 sm:h-9">
                {getStatusIcon((project.status as ProjectStatus) || '進行中')}
                <span className="ml-1 sm:ml-2">{getStatusText((project.status as ProjectStatus) || '進行中')}</span>
              </div>
            )}
          </div>
        )}

        {!isReadOnly && (
          <Button
            variant={editMode ? "outline" : "default"}
            size="sm"
            onClick={onEditToggle}
            className="text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9"
          >
            {editMode ? (
              <>
                <span className="hidden sm:inline mr-1">キャンセル</span>
                <span className="sm:hidden">×</span>
              </>
            ) : (
              <>
                <Edit className="mr-0 sm:mr-1 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">編集</span>
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}; 